/**
 * Minimal external store for high-frequency interaction state.
 *
 * Why not React state / mobx: the keyboard cursor and hover state change many
 * times per second; putting them in component state at the calendar root would
 * re-render the whole grid. With this store only the few leaf components that
 * subscribe to a given slice re-render (via useSyncExternalStore), and the
 * event/columns tree stays completely still.
 */
import { useSyncExternalStore } from "react";

export interface MiniStore<T> {
  get(): T;
  set(partial: Partial<T> | ((prev: T) => Partial<T>)): void;
  subscribe(listener: () => void): () => void;
}

export function createMiniStore<T extends object>(initial: T): MiniStore<T> {
  let state = initial;
  const listeners = new Set<() => void>();

  const get = () => state;

  const set = (partial: Partial<T> | ((prev: T) => Partial<T>)) => {
    const patch = typeof partial === "function" ? partial(state) : partial;
    // Cheap shallow-equal bailout: skip notify when nothing changed.
    let changed = false;
    for (const key of Object.keys(patch) as (keyof T)[]) {
      if (!Object.is(state[key], patch[key])) {
        changed = true;
        break;
      }
    }
    if (!changed) return;
    state = { ...state, ...patch };
    listeners.forEach((l) => l());
  };

  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  return { get, set, subscribe };
}

/**
 * Subscribe to a derived slice of a mini store.
 * The selector MUST return a primitive (or referentially stable value),
 * otherwise useSyncExternalStore will loop.
 */
export function useMiniSelector<T extends object, S>(
  store: MiniStore<T>,
  selector: (state: T) => S,
): S {
  return useSyncExternalStore(store.subscribe, () => selector(store.get()));
}
